// SyncAnomaliesPanel — chunk SG3 (Sync Guard, Alerting).
//
// Renders inside the Admin VCA Data tab, ABOVE the existing PipelinePanel.
// Lists rows from /admin/sync-anomalies (default unacked filter), gives
// per-row Acknowledge action.  Drives the red bubble on the VCA Data tab
// button — parent component (Admin) refetches the count after any ack.
//
// All gating is server-side via Cap.MANAGE_VCA_SYNC.  Component assumes
// the caller has at least that capability (parent only renders this when
// canVca is true).

import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import useCollapse from "./useCollapse";

// Color and human label per anomaly_type.  Matches the severity ordering:
// table_wipe_blocked (most serious — global table about to be wiped) →
// claim_wipe_blocked (per-claim destructive write blocked) →
// row_drop_warning (warn-only, sync proceeded) →
// sync_disabled (informational; SYNC_ENABLED=false).
const ANOMALY_STYLE = {
  table_wipe_blocked: { color: "#dc2626", label: "Table wipe blocked" },
  claim_wipe_blocked: { color: "#ea580c", label: "Claim wipe blocked" },
  claim_missing_in_vca: { color: "#b45309", label: "Claim not in VCA's list" },
  row_drop_warning:   { color: "#f59e0b", label: "Row drop warning" },
  sync_disabled:      { color: "#6b7280", label: "Sync disabled" },
  contact_dup_constraint: { color: "#f59e0b", label: "Contact email collision" },
  // Mig 159: an Atticus edit was kept; VCA holds a different value (Kuhrt 2026-10-26).
  contact_vca_differs:  { color: "#0e7490", label: "VCA differs from edited contact" },
  coverage_vca_changed: { color: "#0e7490", label: "VCA changed an edited coverage line" },
  // Mig 160: the fee invoice refresh left a row alone, or AR no longer matches VCA.
  fee_invoice_mirror: { color: "#b45309", label: "Fee invoice differs from VCA" },
  sg4_drift:          { color: "#7c3aed", label: "Closed-record change" },
  unknown_vca_staff:  { color: "#f59e0b", label: "Unmapped VCA staff" },
};

// How a closed row was closed (mig 155).  One row per standing condition now:
// the sync re-firing a condition bumps "Last seen" / "Nights" instead of adding a row.
const RESOLUTION_LABEL = {
  acknowledged:  "Acknowledged",
  accepted_vca:  "Accepted VCA's version",
  auto_resolved: "Cleared on its own",
  collapsed:     "Duplicate (folded)",
};

function fmtDay(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return s;
  }
}

function fmtTs(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
}

export default function SyncAnomaliesPanel({ onChanged }) {
  const [filter, setFilter] = useState("false"); // "false" | "true" | "all"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Per-row inline ack state: { [id]: { open: bool, note: string, busy: bool } }
  const [ackState, setAckState] = useState({});
  // "Accept VCA's version" confirm: { id, loading, fig, busy, note } — figures come from the server
  const [accept, setAccept] = useState(null);
  const { open, toggle, chevron } = useCollapse(true); // section collapse (default open — actionable)

  const load = () => {
    setLoading(true);
    setErr("");
    apiFetch(`/admin/sync-anomalies?ack=${filter}&limit=200`)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const startAck = (id) =>
    setAckState((s) => ({ ...s, [id]: { open: true, note: "", busy: false } }));

  const cancelAck = (id) =>
    setAckState((s) => ({ ...s, [id]: { ...s[id], open: false } }));

  const setAckNote = (id, note) =>
    setAckState((s) => ({ ...s, [id]: { ...s[id], note } }));

  const submitAck = (id) => {
    setAckState((s) => ({ ...s, [id]: { ...s[id], busy: true } }));
    apiFetch(`/admin/sync-anomalies/${id}/acknowledge`, {
      method: "POST",
      body: { note: ackState[id]?.note || null },
    })
      .then(() => {
        load();
        onChanged?.();  // tell parent to refetch the bubble count
      })
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() =>
        setAckState((s) => ({ ...s, [id]: { open: false, note: "", busy: false } })),
      );
  };

  const startAccept = (id) => {
    setErr("");
    setAccept({ id, loading: true, fig: null, busy: false, note: "" });
    apiFetch(`/admin/sync-anomalies/${id}/accept-preview`)
      .then((fig) => setAccept((a) => (a && a.id === id ? { ...a, loading: false, fig } : a)))
      .catch((e) => { setErr(String(e?.message || e)); setAccept(null); });
  };

  const submitAccept = () => {
    if (!accept?.fig) return;
    const { id, fig, note } = accept;
    setAccept((a) => ({ ...a, busy: true }));
    apiFetch(`/admin/sync-anomalies/${id}/accept-vca`, {
      method: "POST",
      // the figure the dialog showed — the server refuses if the mirror has moved since
      body: { expected_mirror_rows: fig.mirror_rows, note: note || null },
    })
      .then(() => { load(); onChanged?.(); })
      .catch((e) => setErr(String(e?.message || e)))
      .finally(() => setAccept(null));
  };

  const unackedCount = filter === "false" ? rows.length : null;

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div className="section-header" onClick={toggle} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <span className="section-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {chevron}
          Sync Anomalies
          {unackedCount > 0 && (
            <span style={{
              padding: "2px 8px", borderRadius: 10, background: "#dc2626",
              color: "#fff", fontSize: 11, fontWeight: 600,
            }}>
              {unackedCount} unacked
            </span>
          )}
          {unackedCount === 0 && (
            <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 400 }}>
              — all clear
            </span>
          )}
        </span>
        {open && (
        <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="form-control"
            style={{ width: 180, fontSize: 12 }}
          >
            <option value="false">Unacknowledged</option>
            <option value="true">Acknowledged only</option>
            <option value="all">All</option>
          </select>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
        )}
      </div>
      {open && (<>

      {err && <div className="alert-error" style={{ marginTop: 10 }}>{err}</div>}

      {loading ? (
        <div className="loading" style={{ marginTop: 12 }}>Loading anomalies...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 16, color: "var(--text3)", fontSize: 13, textAlign: "center" }}>
          {filter === "false"
            ? "✓ No unacknowledged anomalies."
            : filter === "true"
            ? "No acknowledged anomalies in the last 200 rows."
            : "No anomalies recorded yet."}
        </div>
      ) : (
        <div style={{ marginTop: 10, overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)", textAlign: "left", color: "var(--text3)" }}>
                <th style={{ padding: "6px 8px" }}>First seen</th>
                <th style={{ padding: "6px 8px" }}>Last seen</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Nights</th>
                <th style={{ padding: "6px 8px" }}>Type</th>
                <th style={{ padding: "6px 8px" }}>Table</th>
                <th style={{ padding: "6px 8px" }}>Claim</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Incoming</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Current</th>
                <th style={{ padding: "6px 8px" }}>Notes</th>
                <th style={{ padding: "6px 8px" }}>Status</th>
                <th style={{ padding: "6px 8px" }}>{/* action */}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const style = ANOMALY_STYLE[r.anomaly_type] || { color: "var(--text2)", label: r.anomaly_type };
                const ack = ackState[r.id] || {};
                return (
                  <React.Fragment key={r.id}>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{fmtDay(r.detected_at)}</td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{fmtDay(r.last_seen_at || r.detected_at)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace" }}>{r.occurrences || 1}</td>
                      <td style={{ padding: "6px 8px", color: style.color, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {style.label}
                      </td>
                      <td style={{ padding: "6px 8px", fontFamily: "monospace" }}>{r.table_name}</td>
                      <td style={{ padding: "6px 8px", fontFamily: "monospace", color: "var(--text2)" }}>{r.claim_id || "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace" }}>{r.incoming_count}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace" }}>{r.current_count}</td>
                      <td style={{ padding: "6px 8px", color: "var(--text2)", maxWidth: 360 }}>{r.notes || "—"}</td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {r.acknowledged_at ? (
                          <span style={{ color: "var(--text3)" }} title={r.acknowledged_by_username ? `by ${r.acknowledged_by_username}` : "by the system"}>
                            ✓ {RESOLUTION_LABEL[r.resolution] || "Closed"} · {fmtTs(r.acknowledged_at)}
                          </span>
                        ) : (
                          <span style={{ color: "#dc2626", fontWeight: 600 }}>Open</span>
                        )}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <div style={{ display: "flex", gap: 6, whiteSpace: "nowrap", alignItems: "center" }}>
                          {r.already_cleared && (
                            <span style={{ color: "var(--text3)" }} title="The sync has rewritten these rows since the last block, so Atticus already holds VCA's current data. Nothing to rule on.">
                              Cleared — closes tonight
                            </span>
                          )}
                          {r.can_accept && !ack.open && accept?.id !== r.id && (
                            <button className="btn btn-primary btn-sm" onClick={() => startAccept(r.id)}>
                              {r.anomaly_type === "claim_missing_in_vca" ? "Remove leftover VCA time" : "Accept VCA's version"}
                            </button>
                          )}
                          {!r.acknowledged_at && !r.already_cleared && !ack.open && accept?.id !== r.id && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => startAck(r.id)}
                              title={r.anomaly_type === "claim_wipe_blocked"
                                ? "Keeps blocking: this claim's rows in this table stay frozen — new VCA rows included — and the alert stays quiet for 30 days unless the numbers change."
                                : r.anomaly_type === "claim_missing_in_vca"
                                ? "Leaves the VCA time and expense entries on the claim as they are; the alert stays quiet for 30 days. To retire the whole claim instead, void it from the claim page — this alert then closes itself."
                                : undefined}
                            >
                              {r.anomaly_type === "claim_wipe_blocked" ? "Keep blocking"
                                : r.anomaly_type === "claim_missing_in_vca" ? "Keep as is" : "Acknowledge"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {accept?.id === r.id && (
                      <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--bg2)" }}>
                        <td colSpan={11} style={{ padding: "10px 12px" }}>
                          {accept.loading || !accept.fig ? (
                            <span style={{ fontSize: 12, color: "var(--text2)" }}>Checking the numbers…</span>
                          ) : (
                            accept.fig.anomaly_type === "claim_missing_in_vca" ? (
                            <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.6 }}>
                              <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 4 }}>
                                Remove the leftover VCA time from VCA claim {accept.fig.vca_claim_id}
                                {accept.fig.native_claim_id ? ` (Atticus claim #${accept.fig.native_claim_id})` : ""}?
                              </div>
                              <div>
                                The latest sync walked VCA's whole claim list and this claim was not in it; its VCA record was last
                                refreshed <b>{fmtDay(accept.fig.vca_claim_last_seen)}</b>. The sync only visits claims VCA lists, so nothing
                                about this claim has updated since. Atticus is still holding <b>{accept.fig.mirror_rows}</b> mirrored timelog
                                row{accept.fig.mirror_rows === 1 ? "" : "s"} from before then, which show on the claim
                                as {accept.fig.native_vca_time_rows} VCA time and {accept.fig.native_vca_expense_rows} VCA expense entries.
                              </div>
                              <div style={{ marginTop: 4 }}>
                                Removing sets those {accept.fig.mirror_rows} row{accept.fig.mirror_rows === 1 ? "" : "s"} aside (kept in a backup table);
                                the VCA entries leave the claim at the next nightly reconcile, and return only if VCA lists the claim again.
                                The claim itself and any time entered directly in Atticus are untouched. This removes the time log ONLY:
                                the claim's other VCA-sourced data (reserves, coverage, parties, file notes) stays as it was on that date.
                                If the whole claim should go, cancel and void it from the claim page instead.
                              </div>
                              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                                <input
                                  type="text"
                                  value={accept.note}
                                  onChange={(e) => setAccept((a) => ({ ...a, note: e.target.value }))}
                                  placeholder="Note (optional) — e.g., claim moved to native TPA handling; VCA time was re-entered in Atticus"
                                  className="form-control"
                                  style={{ flex: 1, fontSize: 12 }}
                                  disabled={accept.busy}
                                />
                                <button className="btn btn-primary btn-sm" onClick={submitAccept} disabled={accept.busy}>
                                  {accept.busy ? "Saving..." : `Remove — set aside ${accept.fig.mirror_rows} row${accept.fig.mirror_rows === 1 ? "" : "s"}`}
                                </button>
                                <button className="btn btn-outline btn-sm" onClick={() => setAccept(null)} disabled={accept.busy}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                            ) : (
                            <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.6 }}>
                              <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 4 }}>
                                Accept VCA's version of {accept.fig.table_name} for VCA claim {accept.fig.vca_claim_id}?
                              </div>
                              <div>
                                Atticus is holding <b>{accept.fig.mirror_rows}</b> mirrored row{accept.fig.mirror_rows === 1 ? "" : "s"}; VCA
                                has sent <b>{accept.fig.vca_sends_rows}</b> for <b>{accept.fig.nights_blocked}</b> night{accept.fig.nights_blocked === 1 ? "" : "s"} running
                                (since {fmtDay(accept.fig.first_seen)}). The sync has refused the change each night, which also
                                keeps any NEW VCA rows for this claim out.
                              </div>
                              <div style={{ marginTop: 4 }}>
                                Accepting sets the {accept.fig.mirror_rows} held row{accept.fig.mirror_rows === 1 ? "" : "s"} aside (kept in a backup table)
                                and lets tonight's sync write whatever VCA has.{" "}
                                {accept.fig.vca_sends_rows === 0
                                  ? "VCA is sending none, so after tonight this claim will show none here — they will not come back unless VCA restores them."
                                  : `After tonight this claim should show VCA's ${accept.fig.vca_sends_rows}.`}
                                {accept.fig.table_name === "vca_timelog" && (
                                  <> The claim's {accept.fig.native_vca_time_rows} VCA time and {accept.fig.native_vca_expense_rows} VCA
                                  expense entries in Atticus are rebuilt from the mirror at the next nightly reconcile. If tonight's VCA
                                  fetch fails, the claim shows no VCA entries until the next good night.</>
                                )}
                              </div>
                              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                                <input
                                  type="text"
                                  value={accept.note}
                                  onChange={(e) => setAccept((a) => ({ ...a, note: e.target.value }))}
                                  placeholder="Note (optional) — e.g., confirmed in VCA: entries were moved to another claim"
                                  className="form-control"
                                  style={{ flex: 1, fontSize: 12 }}
                                  disabled={accept.busy}
                                />
                                <button className="btn btn-primary btn-sm" onClick={submitAccept} disabled={accept.busy}>
                                  {accept.busy ? "Saving..." : `Accept — set aside ${accept.fig.mirror_rows} row${accept.fig.mirror_rows === 1 ? "" : "s"}`}
                                </button>
                                <button className="btn btn-outline btn-sm" onClick={() => setAccept(null)} disabled={accept.busy}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                            )
                          )}
                        </td>
                      </tr>
                    )}
                    {ack.open && (
                      <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--bg2)" }}>
                        <td colSpan={11} style={{ padding: "8px 12px" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 12, color: "var(--text2)", whiteSpace: "nowrap" }}>
                              Ack note (optional):
                            </span>
                            <input
                              type="text"
                              value={ack.note || ""}
                              onChange={(e) => setAckNote(r.id, e.target.value)}
                              placeholder="e.g., Confirmed with VCA support — empty payload was a deploy bug, retrying"
                              className="form-control"
                              style={{ flex: 1, fontSize: 12 }}
                              disabled={ack.busy}
                            />
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => submitAck(r.id)}
                              disabled={ack.busy}
                            >
                              {ack.busy ? "Saving..." : "Confirm"}
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => cancelAck(r.id)}
                              disabled={ack.busy}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}
